/**
 * Add / edit one global message. Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this app uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * page-with-back-button + FormGroup field layout) — the same shape as `../app-versions/Form.tsx`.
 *
 * Lengths are checked here as the API checks them (`GlobalMessage.cs`); the API's own 400 lands on
 * the field it names.
 */
import { useState } from "react";
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from "react-router-dom";
import { z } from "zod";
import toast from "react-hot-toast";
import { Textarea } from "rizzui";
import { PiMegaphoneDuotone as MessageIcon, PiToggleRightDuotone as StatusIcon } from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import { PageHeader, ErrorSurface, problemTitle, Field, FormGroup, Form } from "@steelyard/theme";
import { CheckboxCard } from "../../components/checkbox-card";
import { StatusSwitch } from "../../components/status-switch";
import { SectionTitle } from "../../components/section-title";
import { ApiError } from "../../lib/api";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { useGoBack } from "../../lib/navigation";
import { createGlobalMessage, listGlobalMessages, updateGlobalMessage, type GlobalMessage } from "../../lib/services/globalMessages";

export async function newGlobalMessageLoader() {
  return { message: null as GlobalMessage | null };
}

/** `/global-messages/MSG-000012`. A handful of messages, so the list is the lookup. */
export async function editGlobalMessageLoader({ params }: LoaderFunctionArgs) {
  const message = (await listGlobalMessages()).find((m) => m.messageNumber === params.number);
  if (!message) throw new ApiError(404, { title: "No such global message", status: 404 });
  return { message: message as GlobalMessage | null };
}

// The API's own limits (GlobalMessage.TitleMaxLength / BodyMaxLength).
export const TITLE_MAX = 120;
export const BODY_MAX = 1000;

const messageSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(TITLE_MAX, `Title must be ${TITLE_MAX} characters or fewer`),
  body: z.string().trim().min(1, "Body is required").max(BODY_MAX, `Body must be ${BODY_MAX} characters or fewer`),
  dismissible: z.boolean(),
  active: z.boolean(),
});
type MessageFormValues = z.infer<typeof messageSchema>;
type FieldName = keyof MessageFormValues;

/** The API's 400 `errors`, keyed by body property, shown on that field. */
function serverFieldErrors(err: unknown): Partial<Record<FieldName, string>> {
  if (!(err instanceof ApiError)) return {};
  const errors = (err.raw as { errors?: Record<string, string[]> } | null)?.errors ?? {};
  return Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, messages[0]]));
}

export default function GlobalMessageFormPage() {
  const { message } = useLoaderData() as Awaited<ReturnType<typeof editGlobalMessageLoader>>;
  const navigate = useNavigate();
  const goBack = useGoBack("/global-messages");
  const [error, setError] = useState<unknown>(null);
  const [serverErrors, setServerErrors] = useState<Partial<Record<FieldName, string>>>({});

  const title = message ? `Edit ${message.messageNumber}` : "Add Global Message";
  const defaultValues: MessageFormValues = message
    ? { title: message.title, body: message.body, dismissible: message.dismissible, active: message.active }
    : { title: "", body: "", dismissible: true, active: true };

  async function submit(values: MessageFormValues) {
    setError(null);
    setServerErrors({});
    const input = { ...values, title: values.title.trim(), body: values.body.trim() };
    try {
      if (message) await updateGlobalMessage(message.id, input);
      else await createGlobalMessage(input);
      toast.success(`Message ${message ? "updated" : "added"}`);
      navigate("/global-messages");
    } catch (err) {
      setError(err);
      setServerErrors(serverFieldErrors(err));
      toast.error(problemTitle(err, message ? "Could not update this message" : "Could not add this message"));
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader title={title} breadcrumb={[{ name: "Home", href: "/" }, { name: "Website · Global Messages", href: "/global-messages" }, { name: title }]} />

      <Form<MessageFormValues> key={message?.id ?? "new"} validationSchema={messageSchema} useFormProps={{ defaultValues }} className={formCard} onSubmit={submit}>
        {({ register, formState: { errors, isSubmitting } }) => (
          <>
            <div className={formSections}>
              {error ? <ErrorSurface error={error} action={message ? `update ${message.messageNumber}` : "add this message"} /> : null}

              <FormGroup title={<SectionTitle icon={MessageIcon}>Message</SectionTitle>} description="What the customer mobile app shows at the top of its home screen.">
                <Field
                  label="Title"
                  required
                  maxLength={TITLE_MAX}
                  autoComplete="off"
                  wrapperClassName="col-span-full"
                  error={errors.title?.message ?? serverErrors.title}
                  {...register("title")}
                />
                <Textarea
                  label="Body"
                  rows={4}
                  maxLength={BODY_MAX}
                  className="col-span-full"
                  helperText={`Up to ${BODY_MAX} characters.`}
                  error={errors.body?.message ?? serverErrors.body}
                  {...register("body")}
                />
                <CheckboxCard
                  label="Dismissible"
                  helperText="Each reader can close it. Unticked, it stays until it is switched off."
                  className="col-span-full"
                  {...register("dismissible")}
                />
              </FormGroup>

              <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>}>
                <StatusSwitch
                  aria-label="Active"
                  activeText="Shown in the customer app now."
                  inactiveText="Not shown in the app. Switch it on to show it."
                  {...register("active")}
                />
              </FormGroup>
            </div>

            <FormFooter
              className={formCardFooter}
              submitBtnText={isSubmitting ? "Saving…" : "Save changes"}
              isLoading={isSubmitting}
              altBtnText="Cancel"
              handleAltBtn={goBack}
            />
          </>
        )}
      </Form>
    </div>
  );
}
